<template>
  <div class="bitts-quick-filters">
    <div
      class="bitts-quick-filters__controls-row flex flex-row flex-wrap items-center gap-x-4 gap-y-8"
    >
      <slot name="prefix-controls" />
      <!-- Filter select on left (default) -->
      <template v-if="filterSelectPosition === 'left'">
        <BittsQuickFilterSelect
          :disabled="disabled"
          :count="activeFilterNumber"
          :options="filterOptions"
          :selected-options="filters"
          @add-filter="updateFilter"
        >
          <template v-if="$slots['filter-select-prefix']" #prefix="{ option }">
            <slot name="filter-select-prefix" :option="option" />
          </template>
        </BittsQuickFilterSelect>
        <div class="bitts-quick-filters__controls">
          <slot name="controls" />
        </div>
      </template>
      <!-- Filter select on right -->
      <template v-else>
        <div class="bitts-quick-filters__controls">
          <BittsQuickFilterSelect
            :disabled="disabled"
            :count="activeFilterNumber"
            :options="filterOptions"
            :selected-options="filters"
            @add-filter="updateFilter"
          >
            <template
              v-if="$slots['filter-select-prefix']"
              #prefix="{ option }"
            >
              <slot name="filter-select-prefix" :option="option" />
            </template>
          </BittsQuickFilterSelect>
          <slot name="controls" />
        </div>
      </template>
    </div>
    <div class="bitts-quick-filters__filters">
      <BittsQuickFilter
        v-for="filter in filters"
        v-model="filterValues[filter.value]"
        :key="filter.value"
        :disabled="disabled"
        :ref="(el) => setFilterRef(filter.value, el)"
        :filter="filter"
        :selected-text-override="filterTextOverrides[String(filter.value)]"
        :show-operators="filter.showOperators"
        :selected-range="filterRanges[filter.value]"
        :options="filterValueOptions[filter.value] || []"
        :loading="loadingFilterIds.includes(String(filter.value))"
        :operators="operators"
        :operator="filterOperators[filter.value] || 'in'"
        :no-custom-value="filter.noCustomValue || !isSourceFieldFilter(filter)"
        :show-convert-to-advanced="
          typeof showConvertToAdvanced === 'function'
            ? showConvertToAdvanced(filter)
            : showConvertToAdvanced
        "
        @search="onFilterSearch"
        @filter-opened="onFilterOpened"
        @apply-filter="updateFilters"
        @clear-filter="clearFilter(filter.value, filter.datatype)"
        @remove-filter="
          (reload) => removeFilter(filter.value, filter.datatype, reload)
        "
        @update-range-value="(range) => (filterRanges[filter.value] = range)"
        @update-operator="(operator) => updateOperator(filter.value, operator)"
        @closed-filter="onFilterClosed(filter)"
        @tooltip-clicked="(filterId) => emit('tooltip-clicked', filterId)"
        @convert-to-advanced="emit('convert-to-advanced')"
      >
        <template #content>
          <slot
            name="content"
            :filter="filter"
            :filter-values="filterValues"
            :set-filter-value="setFilterValue"
          />
        </template>
        <template #option-prefix-icon="slotProps">
          <slot name="option-prefix-icon" v-bind="slotProps" />
        </template>
        <template #option-suffix-icon="slotProps">
          <slot name="option-suffix-icon" v-bind="slotProps" />
        </template>
        <template
          v-if="hasHoverContentForFilter(filter)"
          #hover-content="slotProps"
        >
          <slot name="hover-content" v-bind="slotProps" />
        </template>
      </BittsQuickFilter>
      <BittsButton
        v-if="filters.length"
        :disabled="disabled"
        text="Remove all"
        variant="ghost"
        size="x-small"
        @click="removeFilters"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { Comment, ComponentPublicInstance, computed, ref, useSlots } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';

import BittsQuickFilter from './BittsQuickFilter.vue';
import BittsQuickFilterSelect from './BittsQuickFilterSelect.vue';
import { FilterMenuNode, FilterProp, Operator, SelectedRange } from './types';

const {
  filterOptions = [],
  filterValueOptions = {},
  disabled = false,
  operators = null,
  showConvertToAdvanced = false,
  filterSelectPosition = 'left',
  filterTextOverrides = {},
  loadingFilterIds = [],
} = defineProps<{
  disabled?: boolean;
  filterOptions: FilterProp[] | FilterMenuNode[];
  filterValueOptions: Record<string, FilterProp[]>;
  operators?: Operator[] | null;
  showConvertToAdvanced?: boolean | ((filter: FilterProp) => boolean);
  filterSelectPosition?: 'left' | 'right';
  filterTextOverrides?: Record<string, string>;
  loadingFilterIds?: string[];
}>();

const emit = defineEmits<{
  'update-filters': [
    filterInfo: {
      filterValues: FilterValues;
      filterRanges: Record<string, SelectedRange>;
      filterOperators: Record<string, string | null>;
      isReset: boolean;
    },
  ];
  'filter-searched': [search: string, filterId: string | number];
  'filter-opened': [filterId: string];
  'update-operator': [filterId: FilterProp['value'], operator: Operator | null];
  'closed-filter': [
    filterInfo: {
      filterValues: FilterValues;
      filterRanges: Record<string, SelectedRange>;
      filterOperators: Record<string, string | null>;
      filterId: string | number;
    },
  ];
  'removed-filter': [filterId: string | number];
  'cleared-filter': [filterId: FilterProp['value']];
  'tooltip-clicked': [filterId: string | number];
  'convert-to-advanced': [];
}>();

defineSlots<{
  'prefix-controls': unknown;
  controls: unknown;
  'filter-select-prefix'(props: { option: FilterProp }): unknown;
  content(props: {
    filter: FilterProp;
    filterValues: Record<
      string,
      (string | number)[] | Record<string, unknown> | null
    >;
    setFilterValue: typeof setFilterValue;
  }): unknown;
  'option-prefix-icon'(props: { item: FilterProp }): unknown;
  'option-suffix-icon'(props: { item: FilterProp }): unknown;
  'hover-content'(props: { filter: FilterProp }): unknown;
}>();

type FilterValues = Record<
  string,
  (string | number)[] | Record<string, unknown> | null
>;

const slots = useSlots();

function hasHoverContentForFilter(filter: FilterProp): boolean {
  if (!slots['hover-content']) return false;
  const nodes = slots['hover-content']({ filter });
  // When a v-if condition is false with no v-else, Vue renders a Comment VNode
  // as a DOM placeholder rather than returning an empty array. We check for any
  // non-Comment node to determine whether the slot has real content.
  return nodes.some((vnode) => vnode.type !== Comment);
}

function isSourceFieldFilter(filter: FilterProp) {
  return Boolean(Number(filter.value));
}

const filters = ref<FilterProp[]>([]);
const filterValues = ref<FilterValues>({});
const filterRanges = ref<Record<string, SelectedRange>>({});
const filterOperators = ref<Record<string, string | null>>({});
const filterRefs = ref<
  Record<string, InstanceType<typeof BittsQuickFilter> | null>
>({});

function setFilterRef(
  filterId: string | number,
  el: ComponentPublicInstance | Element | null,
) {
  if (el) {
    filterRefs.value[String(filterId)] = el as InstanceType<
      typeof BittsQuickFilter
    >;
  } else {
    filterRefs.value[String(filterId)] = null;
  }
}

function updateFilter(filter: FilterProp) {
  if (filters.value.some((f) => f.value === filter.value)) {
    const filterComponent = filterRefs.value[String(filter.value)];
    if (filterComponent?.openDropdown) {
      filterComponent.openDropdown();
    }
  } else {
    filters.value.push(filter);
  }
}

const activeFilterNumber = computed(() => {
  let count = 0;
  Object.keys(filterValues.value).forEach((key) => {
    const value = filterValues.value[key];
    // Logic for counting standard filters
    if (Array.isArray(value) && value.length) count++;
    // Logic for counting object filters (lookup datatype)
    else if (value && typeof value === 'object' && Object.keys(value).length)
      count++;
    else if (value === null && !!filterOperators.value[key]?.includes('empty'))
      count++;
  });
  Object.values(filterRanges.value).forEach((value) => {
    if (value && value.length) count++;
  });
  return count;
});

function updateOperator(
  filterId: FilterProp['value'],
  operator: Operator | null,
) {
  const previousOperator = filterOperators.value[filterId] || 'in';
  filterOperators.value[filterId] = operator?.value || null;
  const newOperator = filterOperators.value[filterId] || 'in';

  const isUsingEmptyOperator = ['is empty', 'is not empty'].includes(
    newOperator,
  );
  if (isUsingEmptyOperator) {
    filterValues.value[filterId] = null;
    return;
  }

  const containsOps = ['contains', 'does not contain'];
  const wasContains = containsOps.includes(previousOperator);
  const isContains = containsOps.includes(newOperator);
  if (wasContains !== isContains) {
    filterValues.value[filterId] = [];
  }

  emit('update-operator', filterId, operator);
}

function updateFilters(isReset = false) {
  emit('update-filters', {
    filterValues: filterValues.value,
    filterRanges: filterRanges.value,
    filterOperators: filterOperators.value,
    isReset,
  });
}

function setFilterValue<
  TKey extends string | number,
  TVal extends Record<string, unknown> | (string | number)[],
>(filterId: TKey, incomingValue: TVal) {
  filterValues.value[String(filterId)] = incomingValue;
}

function resetFilterState(filterId: string | number) {
  delete filterValues.value[filterId];
  delete filterRanges.value[filterId];
  delete filterOperators.value[filterId];
}

function clearFilter(
  filterId: FilterProp['value'],
  datatype: FilterProp['datatype'] | null = null,
) {
  emit('cleared-filter', filterId);
  if (['number', 'datetime'].includes(datatype || '')) {
    delete filterRanges.value[filterId];
    delete filterOperators.value[filterId];
  } else {
    resetFilterState(filterId);
  }
}

function removeFilter(
  filterId: FilterProp['value'],
  datatype: FilterProp['datatype'] | null = null,
  reload = true,
) {
  const filter = filters.value.find((f) => f.value === filterId);
  if (filter?.notRemovable) return;
  clearFilter(filterId, datatype);
  filters.value = filters.value.filter((f) => f.value !== filterId);
  if (reload) updateFilters();
}

function removeFilters() {
  const nonRemovableFilters = filters.value.filter(
    (f) => f.notRemovable === true,
  );
  const nonRemovableIds = new Set(
    nonRemovableFilters.map((f) => String(f.value)),
  );

  Object.keys(filterValues.value).forEach((key) => {
    if (!nonRemovableIds.has(key)) delete filterValues.value[key];
  });
  Object.keys(filterRanges.value).forEach((key) => {
    if (!nonRemovableIds.has(key)) delete filterRanges.value[key];
  });
  Object.keys(filterOperators.value).forEach((key) => {
    if (!nonRemovableIds.has(key)) delete filterOperators.value[key];
  });

  filters.value = nonRemovableFilters;
  updateFilters(true);
}

function onFilterSearch(search: string, filterId: string | number) {
  emit('filter-searched', search, filterId);
}

function onFilterOpened(filterId: string | number) {
  emit('filter-opened', filterId.toString());
}

function onFilterClosed(filter: FilterProp) {
  if (filter.datatype !== 'lookup') {
    return;
  }
  emit('closed-filter', {
    filterValues: filterValues.value,
    filterRanges: filterRanges.value,
    filterOperators: filterOperators.value,
    filterId: filter.value,
  });
}

defineExpose({
  filters,
  filterValues,
  filterRanges,
  filterOperators,
  filterRefs,
});
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-quick-filters {
  @apply flex flex-col gap-8 mb-4;
}

.bitts-quick-filters__filters {
  @apply flex flex-row gap-4 overflow-x-auto;
}

.bitts-quick-filters__controls {
  @apply flex flex-row items-center gap-4 ml-auto;
}
</style>
